/**
 * SRT → WebVTT conversion for local (viewer-only) subtitle tracks.
 *
 * Viewers can upload a .srt/.vtt file that is added only to their own player
 * (no server round-trip). The file is converted to VTT in the browser and
 * loaded via a blob URL so Video.js can play it without a server request.
 */

/**
 * Convert SRT subtitle content to WebVTT.
 *
 * VTT and SRT are nearly identical; the differences are:
 *  1. VTT starts with the header `WEBVTT`.
 *  2. SRT uses commas for decimal milliseconds; VTT uses periods.
 *
 * If the input already looks like VTT (starts with WEBVTT), it is returned
 * unchanged.
 */
export function srtToVtt(srt: string): string {
  const trimmed = srt.trim();
  if (trimmed.startsWith('WEBVTT')) return `${trimmed}\n`;
  const withDots = trimmed.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
  return `WEBVTT\n\n${withDots}\n`;
}

/**
 * Read a File (from an <input type="file">) as text.
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result : '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Create a blob URL for VTT content. The caller should revoke the URL when
 * the track is removed or the player is disposed.
 */
export function vttToBlobUrl(vtt: string): string {
  const blob = new Blob([vtt], { type: 'text/vtt' });
  return URL.createObjectURL(blob);
}
